/**
 * User Form Validation Schema
 * Using Zod for type-safe validation
 */

import { z } from 'zod';
import { UserStatus } from '../../types/domain-enums';

export const userFormSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email address'),
  fullName: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  companyId: z
    .string()
    .min(1, 'Company is required'),
  username: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 3,
      'Username must be at least 3 characters if provided'
    ),
  // No password: admins never set one. Create returns a one-time
  // set-password link the admin shares with the new user instead.
  department: z
    .string()
    .optional(),
  jobTitle: z
    .string()
    .optional(),
  employeeId: z
    .string()
    .optional(),
  phoneNumber: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[\d\s\-\+\(\)]+$/.test(val),
      'Invalid phone number format'
    ),
  // No `.default()` on these three: zod's default makes the OUTPUT type
  // required while the INPUT type stays optional, so `Resolver<input>` and
  // `Resolver<output>` stop matching and react-hook-form's generics no longer
  // line up. That mismatch is what the `// @ts-nocheck` at the top of UserForm
  // was hiding (QA ledger CONSIST-002). The form supplies all three defaults
  // itself, which is where a form's defaults belong.
  // Create mode only: the company role granted in the same request. Optional
  // so a company with no roles can still get users; the modal says so then.
  roleId: z.string().optional(),
  status: z.nativeEnum(UserStatus),
  timezone: z.string(),
  language: z.string(),
});

export type UserFormData = z.infer<typeof userFormSchema>;
